/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive, Input, OnDestroy, OnInit } from '@angular/core';

import { FilteredDataQuery, getSlicedData, SlicedDataSource } from '@gh/core-data';
import { LocalizationService } from '@gh/core-mls';
import { coalesce, notNil, toString } from '@gh/core-util';
import { EMPTY$, interval$, of$ } from '@gh/rx';
import { first$, map$, mapTo$, skipWhile$, startWith$, switchMap$ } from '@gh/rx/operators';

import { RowNode } from 'ag-grid-community';
import { isUndefined } from 'lodash';
import { Observable, Subject, Subscription } from 'rxjs';

import { GridComponent } from '../grid/grid.component';

const NODE_DATA_LOADED_POLLING_INTERVAL = 300;

export interface GridFinderItem {
  id: any;
  name: string;
}

export interface RowPointer {
  rn: number;
  name: string;
}

export abstract class GridFinderSource {
  debounceTime: number;

  abstract onOpen(): void;

  abstract onClose(): void;

  abstract goTo(id: any, select: boolean): void;

  abstract loadMore(query: FilteredDataQuery<string>): Observable<GridFinderItem[]>;
}

@Directive({
  selector: '[ghGridFinderInMemory]',
  providers: [{ provide: GridFinderSource, useExisting: GridFinderInMemoryDirective }],
})
export class GridFinderInMemoryDirective extends GridFinderSource {
  @Input() grid: GridComponent;
  @Input() textField: string;
  @Input('ghGridFinderInMemoryFilterWith') filterWith?: PredicateFn<any>;
  @Input('displayWith') displayWith?: TransformFn<any, string>;

  debounceTime = 0;

  private items?: GridFinderItem[];

  constructor(private localizationService: LocalizationService) {
    super();
  }

  onOpen(): void {
    const { api } = this.grid;
    const { filterWith } = this;

    const items: GridFinderItem[] = [];
    api.forEachNodeAfterFilterAndSort((node: RowNode) => {
      if (!filterWith || filterWith(node.data)) {
        items.push({
          id: node,
          name: this.getFormattedValue(node),
        });
      }
    });

    this.items = items;
  }

  onClose(): void {
    this.items = void 0;
  }

  goTo(node: RowNode, select: boolean): void {
    this.grid.api.ensureNodeVisible(node);
    this.grid.api.flashCells({ rowNodes: [node] });

    if (select) {
      node.setSelected(true, true);
    }
  }

  loadMore({ filter, from, to }: FilteredDataQuery<string>): Observable<GridFinderItem[]> {
    const { items } = this;

    const normalizedFilter = this.normalizeLocalizableString(filter || '');
    return items ?
      of$(items
        .filter((item) => this.normalizeLocalizableString(item.name).indexOf(normalizedFilter) >= 0)
        .slice(from, to)) : EMPTY$;
  }

  private normalizeLocalizableString(str: string): string {
    return this.localizationService.getStringL10nHelper().toLowerCase(str);
  }

  private getFormattedValue(node: RowNode): string {
    const { api } = this.grid;

    const value = api.getValue(this.textField, node);
    const colDef = api.getColumnDef(this.textField);
    if (this.displayWith) {
      return this.displayWith(node.data);
    } else if (colDef.valueFormatter) {
      return colDef.valueFormatter(<any>{
        value,
        node,
        data: node.data,
        colDef,
        column: void 0,
        api,
        columnApi: void 0,
        context: void 0,
      });
    } else {
      return toString(coalesce(value, ''));
    }
  }
}

@Directive({
  selector: '[ghGridFinderDataSource]',
  providers: [{ provide: GridFinderSource, useExisting: GridFinderDataSourceDirective }],
})
export class GridFinderDataSourceDirective extends GridFinderSource implements OnInit, OnDestroy {
  @Input() grid: GridComponent;
  @Input() textField: string;

  debounceTime = 300;

  private wait$$ = new Subject<{ node: RowNode; select: boolean }>();
  private subscription: Subscription;
  private _dataSource: SlicedDataSource<RowPointer>;

  @Input('ghGridFinderDataSource')
  set dataSource(ds: SlicedDataSource<RowPointer>) {
    this._dataSource = ds;
  }

  ngOnInit(): void {
    this.subscription = this.wait$$.pipe(
      switchMap$((event) => interval$(NODE_DATA_LOADED_POLLING_INTERVAL).pipe(// we check if data was loaded into node
        startWith$(0), // in order to check node immediately
        skipWhile$(() => isUndefined(event.node.data)),
        first$(), // if data was loaded take only the first event
        mapTo$(event)))) // get the node
      .subscribe(({ node, select }) => {
        this.grid.api.ensureNodeVisible(node);
        if (select) {
          node.setSelected(true, true);
        }
        this.grid.api.flashCells({ rowNodes: [node] });
      });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onOpen(): void {
    // nothing to do
  }

  onClose(): void {
    // nothing to do
  }

  goTo(rowIndex: number, select: boolean): void {
    this.wait$$.next({
      node: notNil(this.grid.api.getModel().getRow(rowIndex)),
      select,
    });
  }

  loadMore(query: FilteredDataQuery<string>): Observable<GridFinderItem[]> {
    return this._dataSource.select(query).pipe(
      map$(getSlicedData),
      map$((pointers) => pointers.map(({ rn, name }) => ({ id: rn, name }))));
  }
}
